// Partner dashboard › Connected apps (specs 30 §2): apps merchants install
// from an OAuth install link.
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, isAuthPrompt } from "../lib/api";
import { useI18n } from "../lib/i18n";
import type { PartnerApp } from "../lib/apps";
import { Badge, Banner, Button, Card, confirmDialog, Field, fieldError, Input, Modal, Skeleton, Switch, Textarea, useToast } from "../components/ui";
import { Segmented } from "../components/Picker";
import { CopyValue } from "./settings/Webhooks";
import type { Key } from "../locales/en";

// The scopes a connected app can ask for (the API catalog is per store, so
// the partner side lists them here).
const RESOURCES: { key: string; readOnly?: boolean }[] = [
  { key: "products" }, { key: "inventory" }, { key: "locations" }, { key: "customers" }, { key: "orders" }, { key: "draft_orders" },
  { key: "fulfillments" }, { key: "returns" }, { key: "discounts" }, { key: "gift_cards" }, { key: "content" }, { key: "themes" },
  { key: "files" }, { key: "markets" }, { key: "translations" }, { key: "reports", readOnly: true }, { key: "shipping", readOnly: true }, { key: "metaobjects" },
];
const SPECIAL = [["read_all_orders", "all_orders"], ["read_customer_pii", "customer_pii"]] as const;
const API_VERSION = "2026-10";

export function PartnerApps({ org, approved, canManage, canAdmin }: { org: string; approved: boolean; canManage: boolean; canAdmin: boolean }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const toast = useToast();
  const key = ["partner", org, "apps"];
  const q = useQuery({ queryKey: key, enabled: approved, queryFn: () => api<{ data: PartnerApp[] }>(`/admin/v1/partners/${org}/apps`) });
  const [editing, setEditing] = useState<PartnerApp | "new" | null>(null);
  const [secretOf, setSecretOf] = useState<PartnerApp | null>(null);
  const secret = useMutation({
    mutationFn: ({ app, rotate }: { app: PartnerApp; rotate: boolean }) => api<PartnerApp>(`/admin/v1/partners/${org}/apps/${app.id}/secret`, { body: { rotate } }),
    onSuccess: setSecretOf,
    onError: (e) => { if (!isAuthPrompt(e)) toast(e.message, true); },
  });
  const remove = useMutation({
    mutationFn: (app: PartnerApp) => api(`/admin/v1/partners/${org}/apps/${app.id}`, { method: "DELETE" }),
    onSuccess: () => { toast(t("papps.deleted")); void qc.invalidateQueries({ queryKey: key }); },
    onError: (e) => { if (!isAuthPrompt(e)) toast(e.message, true); },
  });
  return (
    <Card title={t("papps.title")} description={t("papps.hint")}
      actions={approved && canManage ? <Button size="sm" onClick={() => setEditing("new")}>{t("papps.create")}</Button> : undefined}>
      {!approved ? <p className="text-sm text-ink-muted">{t("papps.pending")}</p> : q.isPending ? <Skeleton /> : (q.data?.data ?? []).length === 0 ? (
        <p className="text-sm text-ink-muted">{t("common.none")}</p>
      ) : (
        <ul className="-my-2 divide-y divide-line">
          {q.data!.data.map((a) => (
            <li key={a.id} className="space-y-2 py-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="flex items-center gap-2 font-medium">{a.name}<Badge>{t("papps.installs", { count: a.installs })}</Badge></span>
                {canManage && <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => setEditing(a)}>{t("apps.edit")}</Button>
                  {canAdmin && <>
                    <Button size="sm" onClick={() => secret.mutate({ app: a, rotate: false })}>{t("apps.client_secret")}</Button>
                    <Button size="sm" variant="plain" onClick={async () => {
                      if (await confirmDialog({ title: t("papps.delete_title", { name: a.name }), body: t("papps.delete_body"), tone: "critical", confirmLabel: t("common.delete") })) remove.mutate(a);
                    }}><span className="text-danger">{t("common.delete")}</span></Button>
                  </>}
                </div>}
              </div>
              <div className="grid gap-2 text-xs sm:grid-cols-2">
                <div><p className="mb-1 text-ink-muted">{t("apps.client_id")}</p><CopyValue value={a.client_id} /></div>
                <div><p className="mb-1 text-ink-muted">{t("papps.install_url")}</p><CopyValue value={a.install_url} /></div>
              </div>
            </li>
          ))}
        </ul>
      )}
      {editing && <PartnerAppModal org={org} app={editing === "new" ? undefined : editing} onClose={() => setEditing(null)}
        onSaved={(a) => { setEditing(null); void qc.invalidateQueries({ queryKey: key }); if (a.client_secret) setSecretOf(a); else toast(t("apps.saved")); }} />}
      {secretOf?.client_secret && (
        <Modal open title={t("papps.secret_title")} onClose={() => setSecretOf(null)}
          footer={<>
            {canAdmin && <Button onClick={async () => {
              if (await confirmDialog({ title: t("papps.rotate_secret"), body: t("apps.rotate_secret_body"), confirmLabel: t("papps.rotate_secret") })) secret.mutate({ app: secretOf, rotate: true });
            }}>{t("papps.rotate_secret")}</Button>}
            <Button variant="primary" onClick={() => setSecretOf(null)}>{t("apps.done")}</Button>
          </>}>
          <div className="space-y-3">
            <Banner tone="warning">{t("papps.secret_once", { url: `${import.meta.env.VITE_API_URL}/admin/oauth/access_token` })}</Banner>
            <CopyValue value={secretOf.client_secret} />
          </div>
        </Modal>
      )}
    </Card>
  );
}

function PartnerAppModal({ org, app, onClose, onSaved }: { org: string; app?: PartnerApp; onClose: () => void; onSaved: (a: PartnerApp) => void }) {
  const { t } = useI18n();
  const [f, setF] = useState({ name: app?.name ?? "", developer_email: app?.developer_email ?? "", app_url: app?.app_url ?? "",
    redirects: (app?.redirect_urls ?? []).join("\n"), scopes: app?.scopes ?? [] as string[] });
  const save = useMutation({
    mutationFn: () => {
      const body = { name: f.name, developer_email: f.developer_email, app_url: f.app_url, scopes: f.scopes, api_version: app?.api_version ?? API_VERSION,
        redirect_urls: f.redirects.split("\n").map((x) => x.trim()).filter(Boolean) };
      return app ? api<PartnerApp>(`/admin/v1/partners/${org}/apps/${app.id}`, { method: "PUT", body: { ...body, version: app.version } })
        : api<PartnerApp>(`/admin/v1/partners/${org}/apps`, { body });
    },
    onSuccess: onSaved,
  });
  const err = save.error;
  const level = (r: string) => (f.scopes.includes("write_" + r) ? "write" : f.scopes.includes("read_" + r) ? "read" : "none");
  const setLevel = (r: string, v: string) => setF({ ...f, scopes: [...f.scopes.filter((s) => s !== "read_" + r && s !== "write_" + r), ...(v === "none" ? [] : [`${v}_${r}`])] });
  return (
    <Modal open variant="sheet" title={app ? t("papps.edit") : t("papps.create")} onClose={onClose}
      footer={<><Button onClick={onClose}>{t("common.cancel")}</Button><Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>{t("common.save")}</Button></>}>
      <div className="space-y-3">
        {err && !(err instanceof ApiError && err.errors.length) && !isAuthPrompt(err) && <Banner tone="critical">{err.message}</Banner>}
        <Field label={t("apps.name")} error={fieldError(err, "name")}><Input value={f.name} maxLength={100} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t("apps.developer_email")} error={fieldError(err, "developer_email")}><Input type="email" dir="ltr" value={f.developer_email} onChange={(e) => setF({ ...f, developer_email: e.target.value })} /></Field>
          <Field label={t("papps.app_url")} error={fieldError(err, "app_url")}><Input type="url" dir="ltr" placeholder="https://" value={f.app_url} onChange={(e) => setF({ ...f, app_url: e.target.value })} /></Field>
        </div>
        <Field label={t("papps.redirect_urls")} hint={t("papps.redirect_hint")} error={fieldError(err, "redirect_urls")}>
          <Textarea dir="ltr" rows={3} value={f.redirects} onChange={(e) => setF({ ...f, redirects: e.target.value })} placeholder="https://app.example.com/auth/callback" />
        </Field>
        <div>
          <p className="mb-2 text-sm font-medium">{t("apps.access")}</p>
          {fieldError(err, "scopes") && <p className="mb-2 text-xs text-danger">{fieldError(err, "scopes")}</p>}
          <ul className="divide-y divide-line rounded-lg border border-line">
            {RESOURCES.map((r) => (
              <li key={r.key} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                <span className="font-medium text-ink">{t(`apps.scope.${r.key}` as Key)}</span>
                <Segmented size="sm" label={t(`apps.scope.${r.key}` as Key)} value={level(r.key)} onChange={(v) => setLevel(r.key, v)}
                  options={[{ value: "none", label: t("apps.access.none") }, { value: "read", label: t("apps.access.read") },
                    ...(r.readOnly ? [] : [{ value: "write", label: t("apps.access.write") }])]} />
              </li>
            ))}
          </ul>
          <div className="mt-3 space-y-1.5">
            {SPECIAL.map(([k, label]) => (
              <div key={k} className="flex items-center justify-between gap-2 text-sm">
                <span className="text-ink">{t(`apps.scope.${label}` as Key)}</span>
                <Switch label={t(`apps.scope.${label}` as Key)} checked={f.scopes.includes(k)} onChange={(on) => setF({ ...f, scopes: on ? [...f.scopes, k] : f.scopes.filter((x) => x !== k) })} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}
